import type { StateCreator } from 'zustand';
import { DisplayedFile } from 'components/common/gallery/filesStore';

export interface BaseGalleryState<T = object> {
  files: DisplayedFile<T>[];
  galleryOpen: boolean;
  currentSlide: number;
}

export interface BaseGalleryActions<T = object> {
  openGalleryAtSlide: (index: number) => void;
  closeGallery: () => void;
  navigateToSlide: (index: number) => void;
  setFiles: (files: DisplayedFile<T>[]) => void;
  updateFileAtIndex: (index: number, updates: Partial<DisplayedFile<T>>) => void;
  getCurrentFile: () => DisplayedFile<T> | null;
}

export type BaseGallerySlice<T = object> = BaseGalleryState<T> & BaseGalleryActions<T>;

const INITIAL_GALLERY_STATE: BaseGalleryState = {
  files: [],
  galleryOpen: false,
  currentSlide: 0,
};

type GalleryStateCreator<T, S extends BaseGallerySlice<T>> = StateCreator<
  S,
  [],
  [],
  BaseGallerySlice<T>
>;

export function createBaseGallerySlice<T = object, S extends BaseGallerySlice<T> = BaseGallerySlice<T>>(
  set: Parameters<GalleryStateCreator<T, S>>[0],
  get: Parameters<GalleryStateCreator<T, S>>[1]
): BaseGallerySlice<T> {
  return {
    ...INITIAL_GALLERY_STATE,
    files: [] as DisplayedFile<T>[],

    openGalleryAtSlide: (index: number): void => {
      set({ galleryOpen: true, currentSlide: index } as Partial<S>);
    },

    closeGallery: (): void => {
      set({ galleryOpen: false } as Partial<S>);
    },

    navigateToSlide: (index: number): void => {
      const { files } = get();
      if (index >= 0 && index < files.length) {
        set({ currentSlide: index } as Partial<S>);
      }
    },

    setFiles: (files: DisplayedFile<T>[]): void => {
      set({ files } as Partial<S>);
    },

    updateFileAtIndex: (index: number, updates: Partial<DisplayedFile<T>>): void => {
      const { files } = get();
      if (index < 0 || index >= files.length) return;

      const updatedFiles = files.map((file, i) =>
        i === index ? { ...file, ...updates } : file
      );
      set({ files: updatedFiles } as Partial<S>);
    },

    getCurrentFile: (): DisplayedFile<T> | null => {
      const { files, currentSlide } = get();
      return files[currentSlide] ?? null;
    },
  };
}
